import { createMutation, invalidateQueries } from '@amzn/gsce-shared-react-lib';
import {
  UpdateAuditCommand,
  type UpdateAuditCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import { apiHeliosClient, withRetry } from '../../../shared/data';
import { schedulePostMutationRefresh } from '../../../shared/hooks';
import { AuditQueryKeys } from './keys';
import {
  cancelAuditSummaryQueries,
  snapshotAndPatchAuditSummary,
  rollbackAuditSummaryCache,
} from '../logic/patchAuditSummaryCache';
import {
  cancelAuditDetailQueries,
  snapshotAndPatchAuditDetail,
  rollbackAuditDetailCache,
} from '../logic/patchAuditDetailCache';

/**
 * Mutation hook for updating an audit (assignees, status, etc.).
 *
 * Uses optimistic updates via queryClient.setQueryData:
 * - onMutate: finds matching cached summary AND detail queries and
 *   patches both so the UI reflects the change immediately.
 * - onError: rolls back both snapshots, invalidates all caches
 * - onSettled (success): invalidates list caches. Summary and detail
 *   caches are already optimistically patched in onMutate; the list
 *   cache is not patched so it needs invalidation.
 */
export const useUpdateAuditMutation = createMutation(
  (input: UpdateAuditCommandInput) =>
    withRetry(() => apiHeliosClient.send(new UpdateAuditCommand(input))),
  {
    onMutate: async (queryClient, variables) => {
      await Promise.all([
        cancelAuditSummaryQueries(queryClient, variables.auditId),
        cancelAuditDetailQueries(queryClient, variables.auditId),
      ]);
      const summarySnapshots = snapshotAndPatchAuditSummary(
        queryClient,
        variables.auditId,
        variables,
      );
      const detailSnapshots = snapshotAndPatchAuditDetail(
        queryClient,
        variables.auditId,
        variables,
      );
      return { summarySnapshots, detailSnapshots };
    },
    onError: (queryClient, _error, _variables, context) => {
      if (context?.summarySnapshots) {
        rollbackAuditSummaryCache(queryClient, context.summarySnapshots);
      }
      if (context?.detailSnapshots) {
        rollbackAuditDetailCache(queryClient, context.detailSnapshots);
      }
    },
    onSettled: (queryClient, _data, error) => {
      if (error) {
        // Rollback happened - reconcile everything with server truth.
        void invalidateQueries(queryClient, [
          AuditQueryKeys.lists,
          AuditQueryKeys.details,
          AuditQueryKeys.summary,
        ]);
        return;
      }
      // Success - summary and detail were optimistically patched in
      // onMutate; the list cache was not, so it needs invalidation.
      void invalidateQueries(queryClient, [AuditQueryKeys.lists]);
      // ES eventual consistency: the list/detail read models may still return
      // stale status/assignee data after the immediate DDB write, so schedule
      // bounded follow-up refreshes for them.
      //
      // Deliberately NOT invalidating/refetching the summary here. The
      // summary is ES-backed (eventually consistent) while the optimistic
      // patch already wrote the correct, DDB-truth value into its cache. A
      // refetch would pull a stale summary and clobber the patch — flipping a
      // just-completed audit back to IN_PROGRESS until ES catches up, and
      // resetting freshly-saved Pass/Fail question responses mid-audit. Trust
      // the patch; let the existing question-response polling reconcile the
      // summary in the background.
      schedulePostMutationRefresh(queryClient, {
        queryKeys: [AuditQueryKeys.lists, AuditQueryKeys.details],
      });
    },
  },
);
